import type { CampaignRegime } from '../../campaign/campaign.types';
import type { DealPhase } from '../../feed/deal.types';
import { oklchToHex } from '../../shared/colour/oklch';
import { SYMBOL } from './format';

/**
 * The figures both raise pages print about a raise: the live index, /raises
 * (views/raises.hbs), and one raise's page, /raises/{slug} (views/raise.hbs).
 * Money, dates in the reader's zone, how funded a raise is, how soon it
 * closes, its regime, and the tile that stands in for a missing logo.
 *
 * Pure, and free of anything the browser cannot take: the pages' scripts
 * run the same functions on the reader's own clock and time zone.
 */

export const LONDON = 'Europe/London';

const DAY = 24 * 3_600_000;

// ---- Money ------------------------------------------------------------------------

/** "£412,300": the currency's symbol, thousands separated, no decimals. */
export function fullMoney(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency ?? 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(amount);
  } catch {
    // An unknown ISO code: the number with the code rather than nothing.
    return `${Math.round(amount).toLocaleString('en-GB')} ${currency ?? ''}`.trim();
  }
}

/**
 * "£130k", "$1.05M", "PLN 9,500": an amount as the title and the meta
 * description have room for it, with the symbols the site's cards print
 * (format.ts), so USD is "$" where en-GB's Intl writes "US$". Whole below
 * ten thousand; to a tenth of a thousand below a hundred thousand, then
 * whole thousands; millions to two places, then one, then none. Rounded
 * down, so a raise is never shown to have raised more than it has.
 */
export function compactMoney(amount: number, currency: string | null): string {
  const code = currency ?? 'USD';
  const figure =
    amount < 10_000
      ? count(Math.floor(amount))
      : amount < 1e6
        ? `${floored(amount, 1e3, amount < 1e5 ? 1 : 0)}k`
        : amount < 1e9
          ? `${floored(amount, 1e6, amount < 1e7 ? 2 : amount < 1e8 ? 1 : 0)}M`
          : `${floored(amount, 1e9, amount < 1e10 ? 2 : 1)}B`;
  const symbol = SYMBOL[code];
  return symbol ? `${symbol}${figure}` : `${code} ${figure}`;
}

/** `amount` in `unit`s, rounded down to `places`, without trailing zeros. */
function floored(amount: number, unit: number, places: number): string {
  const step = unit / 10 ** places;
  return (Math.floor(amount / step) / 10 ** places).toLocaleString('en-GB', {
    maximumFractionDigits: places,
  });
}

/** A stated, usable amount: a number above zero. */
export const stated = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && value > 0;

/** "1,204": a count with thousands separators. */
export const count = (n: number): string => n.toLocaleString('en-GB');

// ---- Dates ------------------------------------------------------------------------

/** A zone Intl accepts: the one given, or London's. */
export function safeZone(zone: string | null | undefined): string {
  if (!zone) return LONDON;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return zone;
  } catch {
    return LONDON;
  }
}

/**
 * Date parts in `zone`. en-US's names, as the dashboard's format.ts takes
 * them: en-GB's short September is "Sept".
 */
export function parts(
  date: Date,
  zone: string,
  options: Intl.DateTimeFormatOptions,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    ...options,
    timeZone: zone,
    hourCycle: 'h23',
  }).formatToParts(date)) {
    out[p.type] = p.value;
  }
  return out;
}

/**
 * "BST", "CEST", "GMT+9": en-GB knows the European zones' abbreviations,
 * which en-US writes as "GMT+1" (zoneName in src/shared/feed/format.ts).
 */
function zoneAbbr(date: Date, zone: string): string {
  return (
    new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

/** "23:59". */
export function hourMinute(date: Date, zone: string): string {
  const p = parts(date, zone, { hour: '2-digit', minute: '2-digit' });
  return `${p.hour}:${p.minute}`;
}

/** "Thursday 22 October 2026 at 23:59 BST". */
export function fullDate(date: Date, zone: string): string {
  const p = parts(date, zone, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${p.weekday} ${p.day} ${p.month} ${p.year} at ${hourMinute(date, zone)} ${zoneAbbr(date, zone)}`;
}

/** "30 Oct 2026, 14:05 GMT": a moment, with its year and its zone. */
export function dateTimeZone(date: Date, zone: string): string {
  const p = parts(date, zone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${p.day} ${p.month} ${p.year}, ${hourMinute(date, zone)} ${zoneAbbr(date, zone)}`;
}

/** "Thu 22 Oct". */
export function shortDay(date: Date, zone: string): string {
  const p = parts(date, zone, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** "Thu 22": a day without its month, where the month is said once nearby. */
export function weekdayDay(date: Date, zone: string): string {
  const p = parts(date, zone, { weekday: 'short', day: 'numeric' });
  return `${p.weekday} ${p.day}`;
}

/** The calendar day `date` falls on in `zone`, as a day number. */
function calendarDay(date: Date, zone: string): number {
  const p = parts(date, zone, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)) / DAY;
}

/** Calendar days from `a` to `b` in `zone`: Fri 16 Oct → Thu 22 Oct is 6. */
export function daysBetween(a: Date, b: Date, zone: string): number {
  return calendarDay(b, zone) - calendarDay(a, zone);
}

/**
 * Noon on the calendar day `days` after `now`'s, in `zone`: a moment that
 * is surely on that day, for labelling it, whatever the clocks do.
 */
export function dayAfter(now: Date, days: number, zone: string): Date {
  const start = calendarDay(now, zone) + days;
  const guess = new Date(start * DAY + DAY / 2);
  // Within ±14 hours of UTC noon the zone's date is the same day; step once
  // where it is not (Kiribati, the date line's far side).
  const off = calendarDay(guess, zone) - start;
  return off === 0 ? guess : new Date(guess.getTime() - off * DAY);
}

// ---- How funded -------------------------------------------------------------------

/**
 * What the raised figure is measured against: the maximum where one is stated
 * above the target, as the dashboard's bar and design-tokens.md's Progress
 * bars have it, else the target. A target alone overstates a round that may
 * take ten times it: FunderNation's Zendia had €113,200 of a €90,000
 * threshold, which is 125% of the target but 12% of its €900,000 maximum.
 */
export function scaleOf(
  target: number | null,
  max: number | null,
): { amount: number; basis: 'target' | 'maximum' } | null {
  if (stated(max) && !(stated(target) && max <= target)) {
    return { amount: max, basis: 'maximum' };
  }
  return stated(target) ? { amount: target, basis: 'target' } : null;
}

/**
 * How much of its target a raise has raised, as a whole percentage; null
 * when it reports no amount raised or states no target. Rounded, except that
 * a raise still short of its target never rounds up to 100.
 */
export function fundedPct(
  raised: number | null,
  target: number | null,
): number | null {
  if (raised === null || !Number.isFinite(raised) || !stated(target)) {
    return null;
  }
  const pct = Math.round((raised / target) * 100);
  return pct >= 100 && raised < target ? 99 : pct;
}

/**
 * The day a closed raise closed: when it ended (endedAtOf), else its close date
 * once that has passed, since a raise can close early. Null for one nobody
 * dated, and for any other phase.
 */
export function closedDateOf(
  facts: {
    phase: DealPhase;
    closeAt: Date | null;
    endedAt?: Date | null;
  },
  now: Date,
): Date | null {
  if (facts.phase !== 'CLOSED') return null;
  if (facts.endedAt) return facts.endedAt;
  return facts.closeAt && facts.closeAt <= now ? facts.closeAt : null;
}

/** "3,661%". */
export function pctText(pct: number): string {
  return `${count(pct)}%`;
}

/**
 * The target, as a multiple of what a raise past it has raised: "2,701×"
 * from a hundredfold, "37×" from tenfold, "3.6×" below that.
 */
export function multiple(pct: number): string {
  const x = pct / 100;
  const tenth = Math.round(x * 10) / 10;
  if (tenth < 10) return `${tenth.toFixed(1)}×`;
  return `${count(Math.round(x))}×`;
}

/**
 * A progress bar, as /raises and the More raises cards draw it. Below its
 * target, one fill. At or past it, the target as an ink sliver at its share
 * of what was raised, and the rest past-target green. With no amount
 * raised, or no target, an empty dashed track.
 */
export type Bar =
  | { kind: 'below'; fill: number }
  | { kind: 'past'; target: number }
  | { kind: 'unreported' };

/** The narrowest the target's sliver gets, so it never disappears. */
export const MIN_SLIVER = 1.5;

const twoPlaces = (n: number) => Math.round(n * 100) / 100;

export function barOf(raised: number | null, target: number | null): Bar {
  const pct = fundedPct(raised, target);
  if (pct === null) return { kind: 'unreported' };
  const exact = (raised! / target!) * 100;
  if (pct < 100)
    return { kind: 'below', fill: twoPlaces(Math.min(100, exact)) };
  return {
    kind: 'past',
    target: twoPlaces(Math.max(MIN_SLIVER, (100 / exact) * 100)),
  };
}

// ---- How soon it closes -----------------------------------------------------------

/**
 * Whole calendar days from today to the close, in the reader's zone: a
 * raise closing tomorrow at 00:30 is 1 day away tonight at 23:00. Never
 * below 0.
 */
export function daysLeft(close: Date, now: Date, zone: string): number {
  return Math.max(0, daysBetween(now, close, zone));
}

/** ▲ on red closing today or tomorrow, ▲ on amber within a week, plain after. */
export type UrgencyTone = 'final' | 'soon' | 'calm';

export interface Urgency {
  /** "▲ 3 days left". */
  text: string;
  tone: UrgencyTone;
}

export function urgencyOf(days: number): Urgency {
  if (days <= 0) return { text: '▲ Closes today', tone: 'final' };
  if (days === 1) return { text: '▲ Closes tomorrow', tone: 'final' };
  if (days <= 7) return { text: `▲ ${days} days left`, tone: 'soon' };
  return { text: `${days} days left`, tone: 'calm' };
}

// ---- The raise in the app ----------------------------------------------------------

/**
 * The raise's deal page in the Raise Sonar app, under the app's base:
 * "deals/crowdcube/loopwell-b3p5xq", by its source and its slug there.
 * Where both raise pages' "View on the platform" and Details go.
 */
export function appDealPath(source: string, slug: string): string {
  return `deals/${encodeURIComponent(source)}/${encodeURIComponent(slug)}`;
}

// ---- Regime -----------------------------------------------------------------------

/** The rules a raise is offered under, as investors know them. */
export const REGIME_LABELS: Record<CampaignRegime, string | null> = {
  REG_CF: 'Reg CF',
  REG_A: 'Reg A+',
  FCA: 'FCA',
  ECSPR: 'ECSPR',
  CSF: 'CSF',
  NI_45_110: 'NI 45-110',
  OTHER: null,
};

/**
 * "Reg CF · US": the regime and the country it is offered from (the
 * platform's, else the issuer's). Either alone where the other is unknown;
 * null with neither.
 */
export function regimeLabel(
  regime: CampaignRegime,
  country: string | null,
): string | null {
  return [REGIME_LABELS[regime], country].filter(Boolean).join(' · ') || null;
}

// ---- The tile that stands in for a logo -------------------------------------------

/**
 * A raise's initials, at most two: the first letter of the first word that
 * starts with a capital, then of the next capitalised word (a capital and a
 * small letter, so "SEIS" and "LLC" are passed over). With no capital at all,
 * the name's first letter. "Café del Mar Spirits" → "CM", "Haatch SEIS 16" →
 * "H", "stampfree.ai" → "S".
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  const first = words.findIndex((w) => /^\p{Lu}/u.test(w));
  if (first === -1) {
    const letter = name.match(/[\p{L}\p{N}]/u)?.[0];
    return letter ? letter.toUpperCase() : '?';
  }
  const second = words.slice(first + 1).find((w) => /^\p{Lu}\p{Ll}/u.test(w));
  return [words[first], second]
    .filter((w): w is string => Boolean(w))
    .map((w) => [...w][0].toUpperCase())
    .join('');
}

/** The tile's hue, 0–359, from the name, so a raise keeps its colour. */
export function nameHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) % 360;
  }
  return h;
}

/** The tile's colours, as an inline style: a pale wash and a deep letter. */
export function tileStyle(name: string): string {
  const h = nameHue(name);
  return `background: oklch(0.92 0.05 ${h}); color: oklch(0.38 0.09 ${h})`;
}

/** The same two colours in hex, for an email: mail clients drop oklch(). */
export function tileHex(name: string): { background: string; color: string } {
  const h = nameHue(name);
  return {
    background: oklchToHex(0.92, 0.05, h),
    color: oklchToHex(0.38, 0.09, h),
  };
}

/**
 * The description says nothing the name does not ("BOSH!" described as
 * "BOSH!"), so it is left out.
 */
export function sameAsName(description: string, name: string): boolean {
  return description.trim().toLowerCase() === name.trim().toLowerCase();
}
